import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { LOGIN_MUTATION } from '@/graphql/auth.graphql'
import { authService } from '@/services/auth.service'
import { User, Permissions } from '@/types'

interface AuthContextType {
  user: User | null
  permissions: Permissions | null
  loading: boolean
  login: (contactNo: string, password: string) => Promise<void>
  logout: () => void
  updateUser: (userData: Partial<User>) => void
}

interface AuthProviderProps {
  children: React.ReactNode
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [permissions, setPermissions] = useState<Permissions | null>(null)
  const [loading, setLoading] = useState(true)
  const [loginMutation] = useMutation(LOGIN_MUTATION)

  useEffect(() => {
    // Check for existing authentication on app load
    const checkAuth = () => {
      try {
        const token = authService.getValidToken()
        const userData = authService.getUser()
        const permissionsData = authService.getPermissions()
        
        if (token && userData) {
          // Both token and user data exist, user is authenticated
          setUser(userData)
          setPermissions(permissionsData)
        } else {
          // Missing token or user data, clear everything
          authService.removeToken()
          authService.removeUser()
          authService.removePermissions()
          setUser(null)
          setPermissions(null)
        }
      } catch (error) {
        console.error('Authentication check failed:', error)
        authService.removeToken()
        authService.removeUser()
        authService.removePermissions()
        setUser(null)
        setPermissions(null)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()

    // Listen for authentication state changes from auth service
    const handleAuthChange = (isAuthenticated: boolean) => {
      if (!isAuthenticated) {
        setUser(null)
        setPermissions(null)
        setLoading(false)
      }
    }

    authService.addAuthChangeListener(handleAuthChange)

    // Cleanup listener on unmount
    return () => {
      authService.removeAuthChangeListener(handleAuthChange)
    }
  }, [])

  const login = useCallback(async (contactNo: string, password: string) => {
    try {
      const response = await loginMutation({
        variables: { input: { contactNo, password } },
      })

      if (response.data?.login) {
        const { token, user, branches, permissions } = response.data.login
        setUser(user)
        setPermissions(permissions)
        authService.setAuthData(token, user, branches, permissions)
      } else {
        throw new Error('Login failed')
      }
    } catch {
      throw new Error('Login failed')
    }
  }, [loginMutation])

  const logout = useCallback(() => {
    setUser(null)
    setPermissions(null)
    sessionStorage.clear()
    authService.logout()
  }, [])

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...userData } : prev)
  }, [])

  const value = useMemo<AuthContextType>(() => ({
    user,
    permissions,
    loading,
    login,
    logout,
    updateUser,
  }), [user, permissions, loading, login, logout, updateUser])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 